// src/pages/Admin/Techniques/TechniquesPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getAllTechniques,
  deleteTechnique,
} from "../../../services/techniquesServices";
import { getBeltsList } from "../../../services/beltServices";

import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import Button from "../../../components/Button/Button";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { FaPlus, FaSearch, FaBookOpen } from "react-icons/fa";

import AddTechniqueModal from "./AddTechniqueModal";
import EditTechniqueModal from "./EditTechniqueModal";

const DEFAULT_IMG = "/img/no-image.jpg";

// helpers ceintures dynamiques (tri par id)
const beltId = (b) =>
  b?.id ?? b?.belt_id ?? b?.ordre ?? b?.order ?? b?.rank ?? Number.MAX_SAFE_INTEGER;
const beltLabel = (b) =>
  (b && (b.label || b.nom || b.name || b.belt_name || b.couleur || b.color || b.belt)) ||
  String(b || "");

export default function TechniquesPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [families, setFamilies] = useState(["Toutes les familles"]);
  const [selectedFamille, setSelectedFamille] = useState("Toutes les familles");
  const [belts, setBelts] = useState([{ id: -1, label: "Toutes les ceintures" }]);
  const [selectedCeinture, setSelectedCeinture] = useState("Toutes les ceintures");

  // Modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTechnique, setEditingTechnique] = useState(null);

  const fetchTechniques = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllTechniques();
      const list = Array.isArray(data) ? data : [];
      setTechniques(list);

      // familles dynamiques (tri alpha)
      const famSet = new Set();
      list.forEach((t) => {
        const f = (t?.famille || "").trim();
        if (f) famSet.add(f);
      });
      const famArr = ["Toutes les familles", ...Array.from(famSet).sort((a, b) => a.localeCompare(b))];
      setFamilies(famArr);
      if (!famArr.includes(selectedFamille)) setSelectedFamille("Toutes les familles");
    } catch (e) {
      console.error("Erreur techniques:", e);
      toast.error("Erreur lors de la récupération des techniques");
    } finally {
      setLoading(false);
    }
  }, [selectedFamille]);

  const fetchBelts = useCallback(async () => {
    try {
      const res = await getBeltsList();
      const items = Array.isArray(res)
        ? res
            .map((b) => ({ id: beltId(b), label: beltLabel(b)?.trim() }))
            .filter((b) => b.label)
            .sort((a, b) => Number(a.id) - Number(b.id))
        : [];
      const opts = [{ id: -1, label: "Toutes les ceintures" }, ...items];
      setBelts(opts);
      if (!opts.some((o) => o.label === selectedCeinture)) setSelectedCeinture("Toutes les ceintures");
    } catch (e) {
      console.error("Erreur ceintures:", e);
      setBelts([{ id: -1, label: "Toutes les ceintures" }]);
      setSelectedCeinture("Toutes les ceintures");
    }
  }, [selectedCeinture]);

  useEffect(() => { fetchTechniques(); }, [fetchTechniques]);
  useEffect(() => { fetchBelts(); }, [fetchBelts]);

  const filteredTechniques = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return techniques.filter((t) => {
      const matchName = q ? `${t.nom || ""}`.toLowerCase().includes(q) : true;
      const matchFam =
        selectedFamille === "Toutes les familles" || (t.famille || "").trim() === selectedFamille;
      const matchBelt =
        selectedCeinture === "Toutes les ceintures" || (t.ceinture || "").trim() === selectedCeinture;
      return matchName && matchFam && matchBelt;
    });
  }, [techniques, searchTerm, selectedFamille, selectedCeinture]);

  const handleAddClick = () => setShowAddModal(true);
  const handleEditClick = (technique) => { setEditingTechnique(technique); setShowEditModal(true); };
  const handleDeleteClick = async (id) => {
    if (!window.confirm("Supprimer cette technique ?")) return;
    try {
      await deleteTechnique(id);
      setTechniques((prev) => prev.filter((t) => t.id !== id));
      toast.success("Technique supprimée avec succès");
    } catch (e) {
      console.error("Erreur suppression:", e);
      toast.error("Erreur lors de la suppression");
    }
  };

  const countLabel =
    filteredTechniques.length === 0 ? "Aucune technique" :
    filteredTechniques.length === 1 ? "1 technique" :
    `${filteredTechniques.length} techniques`;

  // listes passées aux modales (sans l’option “Toutes …”)
  const familiesForModals = useMemo(
    () => families.filter((f) => f !== "Toutes les familles"),
    [families]
  );

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle role="Enseignants" title="Gestion des Techniques" align="center" className="mb-1" />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <ScrollIndicator />

        {/* ===== Carte principale ===== */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 mb-4">
          {/* Header gradient : titre + bouton ajouter */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start sm:items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaBookOpen />
                </div>
                <div className="text-white">
                  <p className="text-lg sm:text-xl font-semibold">Bibliothèque de techniques</p>
                  <p className="text-white/90 text-sm">Gérer les techniques par famille et ceinture.</p>
                </div>
              </div>

              <Button
                text="Ajouter une technique"
                icon={FaPlus}
                onClick={handleAddClick}
                className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1 self-start sm:self-auto"
              />
            </div>
          </div>

          {/* Contenu blanc */}
          <div className="bg-white dark:bg-slate-900/60 px-4 pb-6 sm:px-6">
            {/* Filtres (desktop: 3 colonnes) */}
            <div className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Recherche par nom */}
                <div className="relative">
                  <input
                    id="search-techniques"
                    type="text"
                    placeholder="Rechercher par nom…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>

                {/* Familles (alpha) */}
                <select
                  value={selectedFamille}
                  onChange={(e) => setSelectedFamille(e.target.value)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {families.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>

                {/* Ceintures (tri id) */}
                <select
                  value={selectedCeinture}
                  onChange={(e) => setSelectedCeinture(e.target.value)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {belts.map((b) => (
                    <option key={`${b.id}-${b.label}`} value={b.label}>{b.label}</option>
                  ))}
                </select>
              </div>

              {/* Compteur sous les filtres */}
              <div className="mt-3">
                <span className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white px-3 py-1 text-sm font-medium shadow">
                  {countLabel}
                </span>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="space-y-3 pt-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24" />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredTechniques.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">Aucune technique trouvée</h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">Ajustez vos filtres ou ajoutez une nouvelle technique.</p>
              </div>
            )}

            {/* Table desktop */}
            {!loading && filteredTechniques.length > 0 && (
              <div className="hidden md:block pt-4">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {["Illustration", "Nom", "Famille", "Ceinture", "Actions"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filteredTechniques.map((t) => (
                          <tr key={t.id} className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
                            <td className="px-4 py-3">
                              <img
                                src={t.image1_url || DEFAULT_IMG}
                                alt={t.nom || "Technique"}
                                onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
                                className="h-20 w-20 rounded-lg object-cover ring-2 ring-white dark:ring-slate-800"
                              />
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                              {t.nom || "—"}
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {t.famille || "—"}
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {t.ceinture || "—"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ButtonEdit onClick={() => handleEditClick(t)} />
                                <ButtonDelete onClick={() => handleDeleteClick(t.id)} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes mobile */}
            {!loading && filteredTechniques.length > 0 && (
              <div className="md:hidden space-y-3 pt-4">
                {filteredTechniques.map((t) => (
                  <div key={`card-${t.id}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <img
                        src={t.image1_url || DEFAULT_IMG}
                        alt={t.nom || "Technique"}
                        onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
                        className="h-24 w-24 rounded-xl object-cover ring-2 ring-white dark:ring-slate-800"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">{t.nom || "—"}</h3>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {t.famille || "—"} • {t.ceinture || "—"}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <ButtonEdit onClick={() => handleEditClick(t)} />
                          <ButtonDelete onClick={() => handleDeleteClick(t.id)} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showAddModal && (
        <AddTechniqueModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          refresh={fetchTechniques}
          existingFamilies={familiesForModals}
        />
      )}
      {showEditModal && editingTechnique && (
        <EditTechniqueModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          refresh={fetchTechniques}
          technique={editingTechnique}
          existingFamilies={familiesForModals}
        />
      )}
    </section>
  );
}
